import { useState, useEffect } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { Sparkles, CheckCircle, Loader2, AlertCircle } from "lucide-react";
import axios from "axios";
import { useTheme } from "../theme";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const { isDark } = useTheme();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("checking"); // checking, paid, failed
  const [eventId, setEventId] = useState(null);
  const navigate = useNavigate();

  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId, 0);
    } else {
      setStatus("failed");
    }
  }, [sessionId]);

  const pollPaymentStatus = async (sid, attempts) => {
    const maxAttempts = 8;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus("failed");
      return;
    }

    try {
      const res = await axios.get(`${API}/checkout/status/${sid}`);
      const data = res.data;

      if (data.payment_status === "paid") {
        setStatus("paid");
        setEventId(data.event_id);
        return;
      }

      if (data.status === "expired") {
        setStatus("failed");
        return;
      }

      // Keep polling
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    } catch (err) {
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
      } else {
        setStatus("failed");
      }
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: isDark ? "#0f172a" : "linear-gradient(135deg, #f8fafc 0%, #eef2ff 50%, #fce7f3 100%)" }} data-testid="payment-success-page">
      <div className="w-full max-w-md text-center">
        <Link to="/" className="inline-flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-pink-500 flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <span className="text-2xl font-black text-indigo-600" style={{ fontFamily: "Nunito" }}>Labelled</span>
        </Link>

        <div className="bg-white/90 backdrop-blur-md rounded-2xl shadow-xl border border-slate-200/50 p-8">
          {status === "checking" && (
            <div className="animate-fade-in" data-testid="payment-checking">
              <Loader2 className="w-16 h-16 text-indigo-600 animate-spin mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-slate-800 mb-2" style={{ fontFamily: "Nunito" }}>
                Processing Payment...
              </h2>
              <p className="text-slate-500">Please wait while we confirm your payment.</p>
            </div>
          )}

          {status === "paid" && (
            <div className="animate-fade-in" data-testid="payment-success">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-emerald-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-800 mb-2" style={{ fontFamily: "Nunito" }}>
                Payment Successful!
              </h2>
              <p className="text-slate-500 mb-6">
                Your event is now featured with top placement, a special badge, and analytics access.
              </p>
              <div className="space-y-3">
                <Link to="/host/dashboard" className="btn-primary w-full inline-flex" data-testid="go-to-dashboard-btn">
                  Go to Dashboard
                </Link>
                <Link to="/" className="btn-secondary w-full inline-flex" data-testid="back-to-map-btn">
                  Back to Map
                </Link>
              </div>
            </div>
          )}

          {status === "failed" && (
            <div className="animate-fade-in" data-testid="payment-failed">
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-10 h-10 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-800 mb-2" style={{ fontFamily: "Nunito" }}>
                Payment Issue
              </h2>
              <p className="text-slate-500 mb-6">
                We couldn't confirm your payment. Please try again or contact support.
              </p>
              <div className="space-y-3">
                <Link to="/host/dashboard" className="btn-primary w-full inline-flex" data-testid="retry-dashboard-btn">
                  Return to Dashboard
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
